import React, { useEffect, useState } from 'react'

import { ThemeProvider } from '@mui/material'

import { getTheme } from 'themes'
import { useTranslation } from 'language/translator'
import { joinWithAnd } from 'utils/functions'

import type {
  TMultiOptionDto,
  TMultiOptionsConfigDto,
  TObjectRowTypesUpdate
} from '@homepal/api-contract'

import { GeneralConfigAttribute } from 'types/GlobalConfig'

import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import Tag from 'components_new/atoms/Tag'
import Text from 'components_new/atoms/Text'

import CheckboxItem from 'components_new/molecules/CheckboxItem'
import EmptyPlaceholder from 'components_new/molecules/EmptyPlaceholder'
import SearchField from 'components_new/molecules/SearchField'
import SectionHeader from 'components_new/molecules/SectionHeader'
import SimpleDialog from 'components_new/molecules/SimpleDialog'

import { getMultiOptionLabel } from 'components_new/organisms/Configs/utils'
import { Highlighted } from 'components_new/organisms/StandardSearch/utils'

interface ObjectRowTypesDialogProps {
  // the object row types config
  general: TMultiOptionsConfigDto | null
  onClose: () => void

  // whether each type shown is excluded, or null to forget it
  onSubmit: (objectRowTypes: TObjectRowTypesUpdate) => void
  open: boolean
}

/**
 * Lets the customer choose which object row types are included. New types
 * come first, and a type the data no longer has can be removed.
 */
const ObjectRowTypesDialog = (props: ObjectRowTypesDialogProps) => {
  const { general, onClose, onSubmit, open } = props

  const { t, translateEnum } = useTranslation()

  const [excluded, setExcluded] = useState<string[]>([])
  const [removed, setRemoved] = useState<string[]>([])
  const [search, setSearch] = useState('')

  useEffect(() => {
    if (open && general) {
      setExcluded(general.value)
      setRemoved([])
      setSearch('')
    }
  }, [open])

  const options = (general?.options ?? []).filter(
    (option) => !removed.includes(option.value)
  )
  const inData = options.filter((option) => !option.missing)
  const matches = options.filter((option) =>
    getMultiOptionLabel(option, t).toLowerCase().includes(search.toLowerCase())
  )

  const newOptions = matches.filter((option) => option.new)
  const knownOptions = matches.filter(
    (option) => !option.new && !option.missing
  )
  const missingOptions = matches.filter((option) => option.missing)

  const newLabels = inData
    .filter((option) => option.new)
    .map((option) => getMultiOptionLabel(option, t))
  const excludedCount = inData.filter((option) =>
    excluded.includes(option.value)
  ).length

  const handleToggle = (type: string) => {
    setExcluded((prevState) =>
      prevState.includes(type)
        ? prevState.filter((item) => item !== type)
        : [...prevState, type]
    )
  }

  const handleSubmit = () => {
    onSubmit(
      Object.fromEntries(
        (general?.options ?? []).map((option) => {
          const type = option.value

          return [type, removed.includes(type) ? null : excluded.includes(type)]
        })
      )
    )
    onClose()
  }

  const getAcknowledgedText = () => {
    return t(
      'configuration',
      newLabels.length === 1
        ? 'acknowledged_on_save_one'
        : 'acknowledged_on_save',
      false,
      { types: joinWithAnd({ t, values: newLabels }) }
    )
  }

  const renderOption = (option: TMultiOptionDto) => {
    const type = option.value
    const included = !excluded.includes(type)

    return (
      <CheckboxItem
        checked={included}
        fullWidth={true}
        hover={true}
        key={type}
        label={
          <Box
            component="span"
            sx={{ alignItems: 'center', display: 'flex', gap: 1 }}
          >
            <Highlighted
              highlight={search}
              text={getMultiOptionLabel(option, t)}
            />
            {option.new ? (
              <Tag color="success" label={t('configuration', 'new_tag')} />
            ) : null}
          </Box>
        }
        onChange={() => handleToggle(type)}
        size="small"
        TextProps={{ color: included ? 'text.primary' : 'text.secondary' }}
      />
    )
  }

  // the types in the data need a heading only next to another section
  const sectioned = newOptions.length > 0 || missingOptions.length > 0

  return (
    <ThemeProvider theme={getTheme('light')}>
      <SimpleDialog
        actions={
          <>
            <Box sx={{ flex: '1 1 auto', mr: 2 }}>
              {newLabels.length > 0 ? (
                <Text color="text.secondary" variant="body2">
                  {getAcknowledgedText()}
                </Text>
              ) : null}
              <Text color="text.secondary" variant="body2">
                {t('configuration', 'takes_effect_after_data_run')}
              </Text>
            </Box>
            <Button onClick={onClose} variant="text">
              {t('global', 'cancel')}
            </Button>
            <Button onClick={handleSubmit}>{t('global', 'save')}</Button>
          </>
        }
        contentText={t('configuration', 'object_row_types_body')}
        disableRestoreFocus={true}
        onClose={onClose}
        open={open}
        title={`${t('global', 'edit')} ${translateEnum(
          'general_config_attributes',
          GeneralConfigAttribute.ObjectRowTypes
        ).title.toLowerCase()}`}
      >
        <Text color="text.secondary" sx={{ mb: 1 }} variant="body2">
          {t('configuration', 'included_summary', false, {
            included: String(inData.length - excludedCount),
            excluded: String(excludedCount)
          })}
        </Text>
        <SearchField
          onClear={() => setSearch('')}
          onSearch={(value) => setSearch(value)}
          size="small"
          sx={{ flex: 0, mb: 2 }}
          value={search}
        />

        {matches.length === 0 ? (
          <EmptyPlaceholder
            height={96}
            title={t('global', 'no_options_found')}
          />
        ) : null}

        {newOptions.length > 0 ? (
          <Box sx={{ mb: 2 }}>
            <SectionHeader
              body={t('configuration', 'new_body')}
              gutterBottom={true}
              title={t('configuration', 'new_since_last_save')}
            />
            {newOptions.map(renderOption)}
          </Box>
        ) : null}

        {knownOptions.length > 0 ? (
          <Box sx={{ mb: 2 }}>
            {sectioned ? (
              <SectionHeader
                gutterBottom={true}
                title={t('configuration', 'in_data')}
              />
            ) : null}
            {knownOptions.map(renderOption)}
          </Box>
        ) : null}

        {missingOptions.length > 0 ? (
          <Box>
            <SectionHeader
              body={t('configuration', 'missing_body')}
              gutterBottom={true}
              title={t('configuration', 'missing_in_data')}
            />
            {missingOptions.map((option) => {
              const type = option.value

              return (
                <Box
                  key={type}
                  sx={{
                    alignItems: 'center',
                    display: 'flex',
                    gap: 1,
                    justifyContent: 'space-between',
                    pl: 1.5
                  }}
                >
                  <Text color="text.secondary">
                    <Highlighted
                      highlight={search}
                      text={getMultiOptionLabel(option, t)}
                    />
                  </Text>
                  <Button
                    onClick={() =>
                      setRemoved((prevState) => [...prevState, type])
                    }
                    size="small"
                    variant="text"
                  >
                    {t('configuration', 'remove')}
                  </Button>
                </Box>
              )
            })}
          </Box>
        ) : null}
      </SimpleDialog>
    </ThemeProvider>
  )
}

export default ObjectRowTypesDialog
